import { PDFDocument, rgb, StandardFonts } from "pdf-lib";
import {
  getIndustryLabel,
  getSubIndustryLabel,
  getCollaborationIntentLabel,
  getCollaborationRoleLabel,
  getWhoAmILabel,
  getAdditionalTechInfraLabel,
  formatRegistrationIdTypeLabel,
  FileCategory,
  regionsData,
  ProductServiceType,
} from "@/components/shared/constants/general.const";
import { getFileUrl } from "@/shared/utils/useFileUrl";
import { getGradeDescription } from "@/data/questionnaireGradeDescriptions";
import { formatCompanyProfileTextForDisplay } from "@/shared/utils/capitalizeLeadingCharacter";
import type { FullGradingResult, QuestionGradingResult } from "@/utils/gradingEngine.types";

// Design constants
const PRIMARY_COLOR = rgb(0.145, 0.388, 0.922); // #2563EB
const GRAY_TEXT = rgb(0.42, 0.45, 0.51); // #6B7280
const BLACK_TEXT = rgb(0.11, 0.11, 0.11); // #111827
const LIGHT_GRAY = rgb(0.96, 0.97, 0.98); // #F3F4F6
const BORDER_COLOR = rgb(0.898, 0.91, 0.922); // #E5E7EB

interface CompanyData {
  legalName?: string;
  gtid?: string;
  headquartersCity?: string;
  headquartersCountry?: string;
  phoneCountryCode?: string;
  phoneNumber?: string;
  email?: string;
  website?: string;
  status?: string;
  description?: string;
  subDescription?: string;
  operations?: string[];
  registrationNumber?: string;
  registrationIdType?: string;
  industryCategory?: string[];
  subIndustry?: string[];
  coreCapabilities?: string[];
  secondaryCapabilities?: string[];
  keyDifferentiators?: string;
  customerOfferHighlight?: string | string[] | null;
  technologies?: string[];
  operatingCountries?: string[];
  preferredCollaborationRegions?: string[];
  productServices?: Array<{
    id: string;
    name: string;
    description?: string;
    type: string;
  }>;
  images?: Array<{
    id: string;
    category: string;
  }>;
  logo?: {
    id: string;
  };
}

const PAGE_MARGIN = 50;
const PAGE_WIDTH = 595.28; // A4 width in points
const PAGE_HEIGHT = 841.89; // A4 height in points
const CONTENT_WIDTH = PAGE_WIDTH - 2 * PAGE_MARGIN;

function getRegionLabel(regionId: string): string {
  const region = regionsData.regions.find((r) => r.id === regionId);
  return region ? region.name : regionId;
}

async function embedImageFromUrl(pdfDoc: PDFDocument, imageUrl: string): Promise<any> {
  try {
    // Try fetching with cors mode first (standard approach)
    let response: Response;
    try {
      response = await fetch(imageUrl, {
        mode: "cors",
        credentials: "omit", // Don't send credentials to avoid CORS preflight
      });
    } catch (corsError) {
      console.warn("CORS fetch failed, trying without CORS restrictions:", corsError);
      return null;
    }

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const imageBytes = await response.arrayBuffer();
    const uint8Array = new Uint8Array(imageBytes);

    // Try PNG first, then JPG
    try {
      return await pdfDoc.embedPng(uint8Array);
    } catch {
      try {
        return await pdfDoc.embedJpg(uint8Array);
      } catch (embedError) {
        console.error("Failed to embed image as PNG or JPG:", embedError);
        return null;
      }
    }
  } catch (error) {
    console.error("Failed to embed image:", error);
    return null;
  }
}

export async function generateProfilePDF(company: CompanyData): Promise<void> {
  const pdfDoc = await PDFDocument.create();
  const helvetica = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const helveticaBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

  let page = pdfDoc.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
  let yPosition = PAGE_HEIGHT - PAGE_MARGIN;

  // Helper function to check if we need a new page
  const checkNewPage = (requiredSpace: number) => {
    if (yPosition - requiredSpace < PAGE_MARGIN) {
      page = pdfDoc.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
      yPosition = PAGE_HEIGHT - PAGE_MARGIN;
      return true;
    }
    return false;
  };

  // Helper function to draw text with wrapping
  const drawText = (
    text: string,
    x: number,
    y: number,
    options: {
      size?: number;
      font?: any;
      color?: any;
      maxWidth?: number;
      lineHeight?: number;
    } = {}
  ): number => {
    const {
      size = 10,
      font = helvetica,
      color = BLACK_TEXT,
      maxWidth = CONTENT_WIDTH,
      lineHeight = size * 1.5,
    } = options;

    const words = text.split(" ");
    const lines: string[] = [];
    let currentLine = "";

    for (const word of words) {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      const testWidth = font.widthOfTextAtSize(testLine, size);

      if (testWidth > maxWidth && currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        currentLine = testLine;
      }
    }
    if (currentLine) {
      lines.push(currentLine);
    }

    let currentY = y;
    for (const line of lines) {
      page.drawText(line, { x, y: currentY, size, font, color });
      currentY -= lineHeight;
    }

    return currentY;
  };

  const drawSectionHeader = (title: string) => {
    checkNewPage(60);
    // Draw background for header
    page.drawRectangle({
      x: PAGE_MARGIN,
      y: yPosition - 5,
      width: CONTENT_WIDTH,
      height: 30,
      color: LIGHT_GRAY,
    });

    page.drawText(title.toUpperCase(), {
      x: PAGE_MARGIN + 10,
      y: yPosition + 4,
      size: 11,
      font: helveticaBold,
      color: BLACK_TEXT,
    });

    yPosition -= 40;
  };

  const drawLabelValue = (label: string, value: string, x: number, width: number) => {
    page.drawText(label.toUpperCase(), {
      x,
      y: yPosition,
      size: 8,
      font: helveticaBold,
      color: GRAY_TEXT,
    });

    // Wrap value if needed
    const wrappedHeight = drawText(value, x, yPosition - 12, {
      size: 10,
      font: helvetica,
      color: BLACK_TEXT,
      maxWidth: width,
    });

    return yPosition - 12 - (yPosition - wrappedHeight);
  };

  // ============= HEADER =============
  // Embed Logo if available
  if (company.logo?.id) {
    try {
      const logoUrl = await getFileUrl(company.logo.id);
      if (logoUrl) {
        const logoImage = await embedImageFromUrl(pdfDoc, logoUrl);
        if (logoImage) {
          const logoSize = 60;
          const aspectRatio = logoImage.width / logoImage.height;
          let width = logoSize;
          let height = logoSize;

          if (aspectRatio > 1) {
            height = width / aspectRatio; // Landscape
          } else {
            width = height * aspectRatio; // Portrait/Square
          }

          page.drawImage(logoImage, {
            x: PAGE_MARGIN,
            y: yPosition - height / 2 - 10,
            width,
            height,
          });
        }
      }
    } catch (err) {
      console.error("Failed to load logo", err);
    }
  }

  // Company Name & Status (Right side of logo or full width if no logo)
  const textStartX = PAGE_MARGIN + 80; // Space for logo
  let textY = yPosition + 10;

  const companyName = company.legalName || "Company Profile";
  page.drawText(companyName, {
    x: textStartX,
    y: textY,
    size: 20,
    font: helveticaBold,
    color: BLACK_TEXT,
  });
  textY -= 25;

  // Status & ID Row
  if (company.gtid) {
    page.drawText(`GTID: ${company.gtid}`, {
      x: textStartX,
      y: textY,
      size: 9,
      font: helvetica,
      color: GRAY_TEXT,
    });
  }
  textY -= 20;

  // Contact Info (Grid Layout)
  const contactY = textY;
  let col1X = textStartX;
  let col2X = textStartX + 180;

  // Column 1
  if (company.headquartersCountry) {
    page.drawText(
      `Location: ${company.headquartersCity ? company.headquartersCity + ", " : ""}${company.headquartersCountry}`,
      {
        x: col1X,
        y: contactY,
        size: 9,
        font: helvetica,
        color: GRAY_TEXT,
      }
    );
  } else {
    page.drawText(`Location: N/A`, { x: col1X, y: contactY, size: 9, font: helvetica, color: GRAY_TEXT });
  }

  if (company.email) {
    page.drawText(`Email: ${company.email}`, {
      x: col1X,
      y: contactY - 15,
      size: 9,
      font: helvetica,
      color: GRAY_TEXT,
    });
  }

  // Column 2
  if (company.website) {
    page.drawText(`Website: ${company.website}`, {
      x: col2X,
      y: contactY,
      size: 9,
      font: helvetica,
      color: PRIMARY_COLOR,
    });
  }

  yPosition -= 100; // Move past header

  // Separator Line
  page.drawLine({
    start: { x: PAGE_MARGIN, y: yPosition },
    end: { x: PAGE_WIDTH - PAGE_MARGIN, y: yPosition },
    thickness: 1,
    color: BORDER_COLOR,
  });
  yPosition -= 30;

  // ============= BUSINESS OVERVIEW =============
  drawSectionHeader("Business Overview");

  // Description
  if (company.description) {
    page.drawText("DESCRIPTION", { x: PAGE_MARGIN, y: yPosition, size: 8, font: helveticaBold, color: GRAY_TEXT });
    yPosition -= 12;
    yPosition = drawText(formatCompanyProfileTextForDisplay(company.description), PAGE_MARGIN, yPosition, {
      size: 10,
      font: helvetica,
      color: BLACK_TEXT,
    });
    yPosition -= 25;
  }

  if (company.subDescription) {
    page.drawText("SUB DESCRIPTION", { x: PAGE_MARGIN, y: yPosition, size: 8, font: helveticaBold, color: GRAY_TEXT });
    yPosition -= 12;
    yPosition = drawText(formatCompanyProfileTextForDisplay(company.subDescription), PAGE_MARGIN, yPosition, {
      size: 10,
      font: helvetica,
      color: BLACK_TEXT,
    });
    yPosition -= 20;
  }

  if (Array.isArray(company.operations) && company.operations.length > 0) {
    page.drawText("OPERATIONS", { x: PAGE_MARGIN, y: yPosition, size: 8, font: helveticaBold, color: GRAY_TEXT });
    yPosition -= 12;
    yPosition = drawText(company.operations.join(", "), PAGE_MARGIN, yPosition, {
      size: 10,
      font: helvetica,
      color: BLACK_TEXT,
    });
    yPosition -= 20;
  }

  // Grid for Details (2 Columns)
  checkNewPage(100);
  const colWidth = (CONTENT_WIDTH - 20) / 2;

  // Row 1
  const row1StartY = yPosition;
  const row1Col1EndY = drawLabelValue("Country", company.headquartersCountry || "N/A", PAGE_MARGIN, colWidth);
  yPosition = row1StartY; // Reset for col 2
  const row1Col2EndY = drawLabelValue(
    "Business ID",
    company.gtid || company.registrationNumber || "N/A",
    PAGE_MARGIN + colWidth + 20,
    colWidth
  );

  // Set Y to lowest point of Row 1 + padding
  yPosition = Math.min(row1Col1EndY, row1Col2EndY) - 25;

  // Row 2
  const row2StartY = yPosition;

  const industryLabel =
    Array.isArray(company.industryCategory) && company.industryCategory.length > 0
      ? company.industryCategory.map((c) => getIndustryLabel(c)).join(", ")
      : "N/A";
  const row2Col1EndY = drawLabelValue("Industry", industryLabel, PAGE_MARGIN, colWidth);

  yPosition = row2StartY; // Reset for col 2

  const subIndustryText =
    Array.isArray(company.subIndustry) && company.subIndustry.length > 0
      ? company.subIndustry.map((sub: string) => getSubIndustryLabel(sub)).join(", ")
      : "N/A";
  const row2Col2EndY = drawLabelValue("Sub-Industry", subIndustryText, PAGE_MARGIN + colWidth + 20, colWidth);

  // Set Y to lowest point of Row 2 + padding
  yPosition = Math.min(row2Col1EndY, row2Col2EndY) - 35;

  // ============= CAPABILITIES =============
  drawSectionHeader("Capabilities & Infrastructure");

  // Core Capabilities (Pill style list)
  page.drawText("CORE CAPABILITIES", { x: PAGE_MARGIN, y: yPosition, size: 8, font: helveticaBold, color: GRAY_TEXT });
  yPosition -= 15;

  if (company.coreCapabilities && company.coreCapabilities.length > 0) {
    // Simulate bulleted list but cleaner
    const caps = company.coreCapabilities.join("  |  ");
    yPosition = drawText(caps, PAGE_MARGIN, yPosition, { size: 10, font: helvetica, color: BLACK_TEXT });
  } else {
    page.drawText("N/A", { x: PAGE_MARGIN, y: yPosition, size: 10, font: helvetica, color: BLACK_TEXT });
    yPosition -= 15;
  }
  yPosition -= 25;

  // Secondary Capabilities
  if (company.secondaryCapabilities && company.secondaryCapabilities.length > 0) {
    page.drawText("SECONDARY CAPABILITIES", {
      x: PAGE_MARGIN,
      y: yPosition,
      size: 8,
      font: helveticaBold,
      color: GRAY_TEXT,
    });
    yPosition -= 15;
    const secCaps = company.secondaryCapabilities.join("  |  ");
    yPosition = drawText(secCaps, PAGE_MARGIN, yPosition, { size: 10, font: helvetica, color: BLACK_TEXT });
    yPosition -= 25;
  }

  // Tech & Infrastructure
  page.drawText("TECHNOLOGY & INFRASTRUCTURE", {
    x: PAGE_MARGIN,
    y: yPosition,
    size: 8,
    font: helveticaBold,
    color: GRAY_TEXT,
  });
  yPosition -= 15;
  const technologies = (company.technologies || []).join(", ") || "N/A";
  yPosition = drawText(technologies, PAGE_MARGIN, yPosition, { size: 10, font: helvetica, color: BLACK_TEXT });
  yPosition -= 35;

  // ============= MARKETS =============
  drawSectionHeader("Geography & Markets");

  // Operating Countries
  page.drawText("OPERATING COUNTRIES", {
    x: PAGE_MARGIN,
    y: yPosition,
    size: 8,
    font: helveticaBold,
    color: GRAY_TEXT,
  });
  yPosition -= 15;
  const operatingCountries = (company.operatingCountries || []).join(", ") || "N/A";
  yPosition = drawText(operatingCountries, PAGE_MARGIN, yPosition, { size: 10, font: helvetica, color: BLACK_TEXT });
  yPosition -= 25;

  // Preferred Regions
  page.drawText("PREFERRED COLLABORATION REGIONS", {
    x: PAGE_MARGIN,
    y: yPosition,
    size: 8,
    font: helveticaBold,
    color: GRAY_TEXT,
  });
  yPosition -= 15;
  const preferredRegions =
    (company.preferredCollaborationRegions || []).map((r) => getRegionLabel(r)).join(", ") || "N/A";
  yPosition = drawText(preferredRegions, PAGE_MARGIN, yPosition, { size: 10, font: helvetica, color: BLACK_TEXT });
  yPosition -= 35;

  // ============= PRODUCTS & SERVICES =============
  const productServices = company.productServices || [];
  const products = productServices.filter((item) => item.type === ProductServiceType.PRODUCT);
  const services = productServices.filter((item) => item.type === ProductServiceType.SERVICE);

  if (products.length > 0 || services.length > 0) {
    drawSectionHeader("Products & Services");

    if (products.length > 0) {
      page.drawText("PRODUCTS", { x: PAGE_MARGIN, y: yPosition, size: 10, font: helveticaBold, color: PRIMARY_COLOR });
      yPosition -= 20;

      for (const product of products) {
        checkNewPage(50);
        // Bullet point style
        page.drawText(product.name || "Untitled", {
          x: PAGE_MARGIN + 10,
          y: yPosition,
          size: 10,
          font: helveticaBold,
          color: BLACK_TEXT,
        });
        yPosition -= 12;

        if (product.description) {
          yPosition = drawText(product.description, PAGE_MARGIN + 10, yPosition, {
            size: 9,
            font: helvetica,
            color: GRAY_TEXT,
            maxWidth: CONTENT_WIDTH - 10,
          });
        }
        yPosition -= 15;
      }
      yPosition -= 15;
    }

    if (services.length > 0) {
      checkNewPage(50);
      page.drawText("SERVICES", { x: PAGE_MARGIN, y: yPosition, size: 10, font: helveticaBold, color: PRIMARY_COLOR });
      yPosition -= 20;

      for (const service of services) {
        checkNewPage(50);
        page.drawText(service.name || "Untitled", {
          x: PAGE_MARGIN + 10,
          y: yPosition,
          size: 10,
          font: helveticaBold,
          color: BLACK_TEXT,
        });
        yPosition -= 12;

        if (service.description) {
          yPosition = drawText(service.description, PAGE_MARGIN + 10, yPosition, {
            size: 9,
            font: helvetica,
            color: GRAY_TEXT,
            maxWidth: CONTENT_WIDTH - 10,
          });
        }
        yPosition -= 15;
      }
    }
    yPosition -= 20;
  }

  // ============= IMAGES =============
  const companyImages = company.images?.filter((img) => img.category === FileCategory.COMPANY_IMAGE) || [];

  if (companyImages.length > 0) {
    checkNewPage(150); // Ensure space for title + at least one row
    drawSectionHeader("Company Gallery");

    const imagesPerRow = 2;
    const imageSize = 160;
    const imageGap = 20;

    // Calculate centered starting X
    const totalRowWidth = imageSize * imagesPerRow + imageGap * (imagesPerRow - 1);
    const startX = (PAGE_WIDTH - totalRowWidth) / 2;

    for (let i = 0; i < companyImages.length; i += imagesPerRow) {
      checkNewPage(imageSize + 30);

      for (let j = 0; j < imagesPerRow && i + j < companyImages.length; j++) {
        const image = companyImages[i + j];
        try {
          const imageUrl = await getFileUrl(image.id);
          if (!imageUrl) continue;

          const embeddedImage = await embedImageFromUrl(pdfDoc, imageUrl);

          if (embeddedImage) {
            const xPos = startX + j * (imageSize + imageGap);
            const aspectRatio = embeddedImage.width / embeddedImage.height;

            // Fit within box, maintaining aspect ratio
            let imgWidth = imageSize;
            let imgHeight = imageSize / aspectRatio;

            if (imgHeight > imageSize) {
              imgHeight = imageSize;
              imgWidth = imageSize * aspectRatio;
            }

            // Center in the 160x160 box
            const xOffset = (imageSize - imgWidth) / 2;
            const yOffset = (imageSize - imgHeight) / 2;

            page.drawImage(embeddedImage, {
              x: xPos + xOffset,
              y: yPosition - imageSize + yOffset,
              width: imgWidth,
              height: imgHeight,
            });

            // Optional border around image
            page.drawRectangle({
              x: xPos,
              y: yPosition - imageSize,
              width: imageSize,
              height: imageSize,
              borderColor: BORDER_COLOR,
              borderWidth: 1,
            });
          }
        } catch (error) {
          console.error("Failed to embed image:", error);
        }
      }

      yPosition -= imageSize + imageGap;
    }
  }

  // Footer / Page Numbers
  const pages = pdfDoc.getPages();
  pages.forEach((p, idx) => {
    p.drawText(`${idx + 1} / ${pages.length}`, {
      x: PAGE_WIDTH - 50,
      y: 20,
      size: 8,
      font: helvetica,
      color: GRAY_TEXT,
    });
  });

  // Save and download
  const pdfBytes = await pdfDoc.save();
  const blob = new Blob([pdfBytes as BlobPart], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${company.legalName || "Company"}_Profile.pdf`;
  link.click();
  URL.revokeObjectURL(url);
}

// =====================================================================
// COLLABORATION ALIGNMENT REPORT
// =====================================================================

export interface SharedDoc {
  fileName: string;
  isOwnDocument: boolean;
  sharedBy: { legalName: string; };
}

type QuestionOptionEntry = { type: string; options: Array<{ id: string; label: string; }>; };

function resolveAnswerLabel(entry: QuestionOptionEntry, answer: any): string {
  if (!answer) return "-";
  if (entry.type === "multi_select") {
    const ids: string[] = answer.selectedOptionIds ?? [];
    if (ids.length === 0) return "—";
    return ids.map((id) => entry.options.find((o) => o.id === id)?.label ?? id).join(", ");
  }
  const id: string = answer.selectedOptionId;
  if (!id) return "—";
  return entry.options.find((o) => o.id === id)?.label ?? id;
}

export async function generateCollaborationAlignmentReport({
  partyA,
  partyB,
  sharedDocuments,
  matchId,
  collaborationType,
  requesterRole,
  expectedPartnerRole,
  requesterCompanyId,
  expressInterestTitle,
  expressInterestSummary,
  gradingResult,
  sectionTitleMap,
  myAnswers,
  partnerAnswers,
  questionOptionMap,
}: {
  partyA: any;
  partyB: any;
  sharedDocuments: SharedDoc[];
  matchId: string;
  collaborationType?: string;
  requesterRole?: string;
  expectedPartnerRole?: string;
  requesterCompanyId?: string;
  expressInterestTitle?: string | null;
  expressInterestSummary?: string | null;
  gradingResult?: FullGradingResult | null;
  sectionTitleMap?: Record<string, string>;
  myAnswers?: Record<string, any>;
  partnerAnswers?: Record<string, any>;
  questionOptionMap?: Record<string, QuestionOptionEntry>;
}): Promise<void> {
  const pdfDoc = await PDFDocument.create();
  const helvetica = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const helveticaBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
  const helveticaOblique = await pdfDoc.embedFont(StandardFonts.HelveticaOblique);

  const sanitizeText = (text: string): string => {
    if (!text) return "";

    return text
      .replace(/\u2011/g, "-") // non-breaking hyphen
      .replace(/\u2013|\u2014/g, "-") // en dash, em dash
      .replace(/\u2018|\u2019/g, "'") // smart quotes
      .replace(/\u201C|\u201D/g, '"') // double quotes
      .replace(/[^\x00-\xFF]/g, ""); // strip unsupported unicode
  };
  // Embed logo
  let logoImage: Awaited<ReturnType<typeof pdfDoc.embedPng>> | null = null;
  let logoDims = { width: 0, height: 0 };
  try {
    const logoBytes = await fetch("/GT logo.png").then((r) => r.arrayBuffer());
    logoImage = await pdfDoc.embedPng(logoBytes);
    const LOGO_H = 45;
    logoDims = { width: logoImage.width * (LOGO_H / logoImage.height), height: LOGO_H };
  } catch {
    // logo unavailable – skip silently
  }

  let page = pdfDoc.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
  let yPosition = PAGE_HEIGHT - PAGE_MARGIN;

  const BORDER = rgb(0, 0, 0);
  const CELL_PAD = 5;
  const FS = 9;
  const LH = FS * 1.4;
  const MIN_ROW_H = 20;

  const checkNewPage = (needed: number) => {
    if (yPosition - needed < PAGE_MARGIN) {
      page = pdfDoc.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
      yPosition = PAGE_HEIGHT - PAGE_MARGIN;
    }
  };

  // Word-wrap a string into lines that fit within maxWidth
  const splitLines = (text: string, maxWidth: number, font: any, size: number): string[] => {
    text = sanitizeText(text);
    const words = (text || "N/A").split(" ");
    const lines: string[] = [];
    let cur = "";
    for (const w of words) {
      const test = cur ? `${cur} ${w}` : w;
      if (font.widthOfTextAtSize(test, size) > maxWidth && cur) {
        lines.push(cur);
        cur = w;
      } else {
        cur = test;
      }
    }
    if (cur) lines.push(cur);
    return lines.length ? lines : ["N/A"];
  };

  // Draw wrapped text with per-line page-break handling. Returns new y.
  const drawWrapped = (
    text: string,
    x: number,
    y: number,
    maxWidth: number,
    font: any,
    size: number,
    color: any
  ): number => {
    text = sanitizeText(text);
    const lines = splitLines(text, maxWidth, font, size);
    const lh = size * 1.5;
    let ty = y;
    for (const l of lines) {
      if (ty - lh < PAGE_MARGIN) {
        page = pdfDoc.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
        ty = PAGE_HEIGHT - PAGE_MARGIN;
      }
      page.drawText(l, { x, y: ty, size, font, color });
      ty -= lh;
    }
    return ty;
  };

  // Draw a table. Header row uses LIGHT_GRAY; data rows white. All cells have black borders.
  // Returns nothing; updates yPosition.
  const drawTable = (headers: string[], rows: string[][], colWidths: number[]) => {
    checkNewPage(MIN_ROW_H * 3); // ensure space for at least header + one row

    // Header row
    let xOff = PAGE_MARGIN;
    for (let i = 0; i < headers.length; i++) {
      page.drawRectangle({
        x: xOff,
        y: yPosition - MIN_ROW_H,
        width: colWidths[i],
        height: MIN_ROW_H,
        color: LIGHT_GRAY,
        borderColor: BORDER,
        borderWidth: 0.5,
      });
      page.drawText(headers[i], {
        x: xOff + CELL_PAD,
        y: yPosition - FS - CELL_PAD,
        size: FS,
        font: helveticaBold,
        color: BLACK_TEXT,
      });
      xOff += colWidths[i];
    }
    yPosition -= MIN_ROW_H;

    // Data rows
    for (const row of rows) {
      const allLines = row.map((cell, i) =>
        splitLines(sanitizeText(cell || "N/A"), colWidths[i] - CELL_PAD * 2, helvetica, FS)
      );
      const maxLines = Math.max(1, ...allLines.map((l) => l.length));
      const rowH = Math.max(MIN_ROW_H, maxLines * LH + CELL_PAD * 2);

      checkNewPage(rowH);
      xOff = PAGE_MARGIN;
      for (let i = 0; i < row.length; i++) {
        page.drawRectangle({
          x: xOff,
          y: yPosition - rowH,
          width: colWidths[i],
          height: rowH,
          color: rgb(1, 1, 1),
          borderColor: BORDER,
          borderWidth: 0.5,
        });
        let ty = yPosition - FS - CELL_PAD;
        for (const line of allLines[i]) {
          page.drawText(line, { x: xOff + CELL_PAD, y: ty, size: FS, font: helvetica, color: BLACK_TEXT });
          ty -= LH;
        }
        xOff += colWidths[i];
      }
      yPosition -= rowH;
    }
    yPosition -= 8;
  };

  // Bold section header with light-gray background
  const drawSectionHeader = (title: string, subtitle?: string) => {
    checkNewPage(120); // reserve space for header + at least a few lines of content so header is never orphaned
    // Rect drawn entirely BELOW current yPosition to avoid overlapping prior content
    page.drawRectangle({ x: PAGE_MARGIN, y: yPosition - 30, width: CONTENT_WIDTH, height: 28, color: LIGHT_GRAY });
    page.drawText(title, { x: PAGE_MARGIN + 8, y: yPosition - 20, size: 11, font: helveticaBold, color: BLACK_TEXT });
    if (subtitle) {
      const titleW = helveticaBold.widthOfTextAtSize(title, 11);
      page.drawText(subtitle, {
        x: PAGE_MARGIN + 8 + titleW + 6,
        y: yPosition - 20,
        size: 9,
        font: helveticaOblique,
        color: GRAY_TEXT,
      });
    }
    yPosition -= 38;
  };

  // Smaller bold subsection line
  const drawSubHeader = (title: string) => {
    checkNewPage(30);
    page.drawText(title, { x: PAGE_MARGIN, y: yPosition, size: 10, font: helveticaBold, color: BLACK_TEXT });
    yPosition -= 6;
    page.drawLine({
      start: { x: PAGE_MARGIN, y: yPosition },
      end: { x: PAGE_WIDTH - PAGE_MARGIN, y: yPosition },
      thickness: 0.5,
      color: BORDER_COLOR,
    });
    yPosition -= 14;
  };

  // ===== REPORT HEADER =====
  const today = new Date();
  const reportDate = today.toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });
  const caseRef = `GTSETU/CASE/${matchId.slice(-8).toUpperCase()}`;


  page.drawText("Collaboration Alignment Report", {
    x: PAGE_MARGIN,
    y: yPosition,
    size: 18,
    font: helveticaBold,
    color: BLACK_TEXT,
  });
  yPosition -= 20;
  page.drawText("Confidential Report – Generated via the GTsetu Platform", {
    x: PAGE_MARGIN,
    y: yPosition,
    size: 10,
    font: helveticaBold,
    color: BLACK_TEXT,
  });
  yPosition -= 6;
  page.drawLine({
    start: { x: PAGE_MARGIN, y: yPosition },
    end: { x: PAGE_WIDTH - PAGE_MARGIN, y: yPosition },
    thickness: 1,
    color: BORDER_COLOR,
  });
  yPosition -= 20;

  // ===== SECTION 1: REPORT OVERVIEW =====
  drawSectionHeader("1. Report Overview");

  page.drawText("Report Title:", { x: PAGE_MARGIN, y: yPosition, size: FS, font: helveticaBold, color: BLACK_TEXT });
  page.drawText("Collaboration Alignment Report", {
    x: PAGE_MARGIN + 70,
    y: yPosition,
    size: FS,
    font: helvetica,
    color: BLACK_TEXT,
  });
  yPosition -= LH + 2;
  page.drawText("Platform:", { x: PAGE_MARGIN, y: yPosition, size: FS, font: helveticaBold, color: BLACK_TEXT });
  page.drawText("GTsetu – Structured Global Collaboration Platform", {
    x: PAGE_MARGIN + 70,
    y: yPosition,
    size: FS,
    font: helvetica,
    color: BLACK_TEXT,
  });
  yPosition -= LH + 8;

  drawTable(
    ["Item", "Details"],
    [
      ["Case Reference Number", caseRef],
      ["Report Date", reportDate],
      ["Collaboration Type", requesterRole && expectedPartnerRole
        ? `${getCollaborationRoleLabel(requesterRole)} / ${getCollaborationRoleLabel(expectedPartnerRole)}`
        : collaborationType || "N/A"],
      ["Participating Companies", `${partyA.legalName || "Party A"} & ${partyB.legalName || "Party B"}`],
    ],
    [160, CONTENT_WIDTH - 160]
  );

  yPosition -= 10;
  checkNewPage(LH + 10);
  page.drawText("Participating Companies", {
    x: PAGE_MARGIN,
    y: yPosition,
    size: 10,
    font: helveticaBold,
    color: BLACK_TEXT,
  });
  yPosition -= 18;

  drawTable(
    ["Company", "Country", "Business Type"],
    [
      [partyA.legalName || "N/A", partyA.headquartersCountry || "N/A", "N/A"],
      [partyB.legalName || "N/A", partyB.headquartersCountry || "N/A", "N/A"],
    ],
    [220, 120, CONTENT_WIDTH - 340]
  );

  // ===== SECTION 2: ABOUT GTsetu =====
  drawSectionHeader("2. About GTsetu");

  const aboutParas = [
    "GTsetu is a structured collaboration platform designed to help companies explore potential business partnerships through a standardized compatibility assessment framework. The platform enables companies to connect, define collaboration intent, and evaluate alignment across technical, commercial, legal, and financial parameters through a structured questionnaire process.",
    "The purpose of the platform is to provide early-stage clarity and facilitate informed discussions between potential partners before detailed negotiations or commercial arrangements begin.",
    "GTsetu's role is limited to enabling connections and generating structured alignment insights based on information voluntarily provided by participating companies. The platform does not participate in negotiations, provide advisory opinions, or validate business claims made by either party.",
  ];
  for (const para of aboutParas) {
    checkNewPage(40);
    yPosition = drawWrapped(para, PAGE_MARGIN, yPosition, CONTENT_WIDTH, helvetica, FS, BLACK_TEXT);
    yPosition -= 6;
  }
  yPosition -= 6;

  // ===== SECTION 3: VERIFICATION SUMMARY =====
  drawSectionHeader("3. Initial Company Verification Summary", "  (Conducted During Onboarding)");

  checkNewPage(50);
  yPosition = drawWrapped(
    "As part of the onboarding process, GTsetu conducts a preliminary verification of publicly available corporate registration details to confirm the identity of participating entities. This verification is limited to basic corporate information and does not constitute financial, legal, or operational due diligence.",
    PAGE_MARGIN,
    yPosition,
    CONTENT_WIDTH,
    helvetica,
    FS,
    BLACK_TEXT
  );
  yPosition -= 8;

  checkNewPage(LH);
  page.drawText("Verification parameters include:", {
    x: PAGE_MARGIN,
    y: yPosition,
    size: FS,
    font: helvetica,
    color: BLACK_TEXT,
  });
  yPosition -= LH;
  for (const param of [
    "Company Name",
    "Company Registration Number",
    "Date of registration of certificate",
    "Registered Address",
    "Company Type / Legal Structure",
    "Current Company Status (Active / Dormant / Inactive)",
  ]) {
    checkNewPage(14);
    page.drawText(`•  ${param}`, { x: PAGE_MARGIN + 16, y: yPosition, size: FS, font: helvetica, color: BLACK_TEXT });
    yPosition -= LH;
  }
  yPosition -= 8;

  const getAddress = (c: any) =>
    c.additionalDetails?.companyRegisteredAddress ||
    c.currentAddress ||
    (c.headquartersCity && c.headquartersCountry ? `${c.headquartersCity}, ${c.headquartersCountry}` : "N/A");
  const getStatus = (c: any) => c.additionalDetails?.companyStatus || (c.status ? String(c.status) : "N/A");

  // 3.1 Party A
  checkNewPage(180);
  drawSubHeader(`3.1 ${partyA.legalName || "Party A"} – Verification Summary`);
  drawTable(
    ["Verification Parameter", "Details"],
    [
      ["Company Name", partyA.legalName || "N/A"],
      ["Company Registration Number", partyA.registrationNumber || "N/A"],
      ["Date of Incorporation", partyA.yearOfIncorporation ? String(partyA.yearOfIncorporation) : "N/A"],
      ["Registered Address", getAddress(partyA)],
      [
        "Company Type",
        formatRegistrationIdTypeLabel(partyA.registrationIdType, partyA.headquartersCountry),
      ],
      ["Company Status", getStatus(partyA)],
    ],
    [180, CONTENT_WIDTH - 180]
  );
  checkNewPage(16);
  page.drawText("Verification Source: Public corporate registry or official records.", {
    x: PAGE_MARGIN,
    y: yPosition,
    size: 8,
    font: helveticaOblique,
    color: GRAY_TEXT,
  });
  yPosition -= 18;

  // 3.2 Party B
  checkNewPage(180);
  drawSubHeader(`3.2 ${partyB.legalName || "Party B"} – Verification Summary`);
  drawTable(
    ["Verification Parameter", "Details"],
    [
      ["Company Name", partyB.legalName || "N/A"],
      ["Company Registration Number", partyB.registrationNumber || "N/A"],
      ["Date of registration of certificate", partyB.yearOfIncorporation ? String(partyB.yearOfIncorporation) : "N/A"],
      ["Registered Address", getAddress(partyB)],
      [
        "Company Type",
        formatRegistrationIdTypeLabel(partyB.registrationIdType, partyB.headquartersCountry),
      ],
      ["Company Status", getStatus(partyB)],
    ],
    [180, CONTENT_WIDTH - 180]
  );
  checkNewPage(16);
  page.drawText("Verification Source: Public corporate registry or official records.", {
    x: PAGE_MARGIN,
    y: yPosition,
    size: 8,
    font: helveticaOblique,
    color: GRAY_TEXT,
  });
  yPosition -= 18;

  // ===== SECTION 4: COMPANY PROFILES =====
  drawSectionHeader("4. Company Profiles (Revealed After Mutual Interest)");

  const getRegionLabel = (regionId: string): string => {
    const region = regionsData.regions.find((r: any) => r.id === regionId);
    return region ? region.name : regionId;
  };

  const drawField = (label: string, value: string) => {
    checkNewPage(LH * 3);
    page.drawText(label, { x: PAGE_MARGIN, y: yPosition, size: 8, font: helveticaBold, color: GRAY_TEXT });
    yPosition -= 12;
    yPosition = drawWrapped(value || "N/A", PAGE_MARGIN, yPosition, CONTENT_WIDTH, helvetica, FS, BLACK_TEXT);
    yPosition -= 10;
  };

  const drawTwoColFields = (pairs: [string, string][]) => {
    const colW = (CONTENT_WIDTH - 20) / 2;
    for (let i = 0; i < pairs.length; i += 2) {
      const left = pairs[i];
      const right = pairs[i + 1];
      const leftLines = splitLines(left[1] || "N/A", colW, helvetica, FS);
      const rightLines = right ? splitLines(right[1] || "N/A", colW, helvetica, FS) : [];
      const rowH = 8 + 12 + Math.max(leftLines.length, rightLines.length, 1) * LH + 10;
      checkNewPage(rowH);

      page.drawText(left[0], { x: PAGE_MARGIN, y: yPosition, size: 8, font: helveticaBold, color: GRAY_TEXT });
      if (right) {
        page.drawText(right[0], { x: PAGE_MARGIN + colW + 20, y: yPosition, size: 8, font: helveticaBold, color: GRAY_TEXT });
      }
      yPosition -= 12;

      let ty = yPosition;
      for (const l of leftLines) {
        page.drawText(l, { x: PAGE_MARGIN, y: ty, size: FS, font: helvetica, color: BLACK_TEXT });
        ty -= LH;
      }
      if (right) {
        let ty2 = yPosition;
        for (const l of rightLines) {
          page.drawText(l, { x: PAGE_MARGIN + colW + 20, y: ty2, size: FS, font: helvetica, color: BLACK_TEXT });
          ty2 -= LH;
        }
        ty = Math.min(ty, ty2);
      }
      yPosition = ty - 10;
    }
  };

  const expressTitleTrim =
    expressInterestTitle != null && String(expressInterestTitle).trim() !== ""
      ? sanitizeText(String(expressInterestTitle).trim())
      : "";
  const expressSummaryTrim =
    expressInterestSummary != null && String(expressInterestSummary).trim() !== ""
      ? sanitizeText(String(expressInterestSummary).trim())
      : "";

  const drawCompanyProfile = (c: any, isPartyA: boolean) => {
    // ── Collaboration Intent Details ──────────────────────────────────────
    const isRequesterProfile = Boolean(requesterCompanyId && c?.id === requesterCompanyId);
    const showExpressCopy = isRequesterProfile && (expressTitleTrim !== "" || expressSummaryTrim !== "");
    if (requesterRole || expectedPartnerRole || showExpressCopy) {
      checkNewPage(20);
      const intentParts: string[] = [];
      if (showExpressCopy) {
        if (expressTitleTrim !== "") {
          intentParts.push(`Title: ${expressTitleTrim}`);
        }
        if (expressSummaryTrim !== "") {
          intentParts.push(`Summary: ${expressSummaryTrim}`);
        }
      }
      if (Array.isArray(c.collaborationIntent) && c.collaborationIntent.length > 0) {
        intentParts.push(`Collaboration Type: ${c.collaborationIntent.map((i: string) => getCollaborationIntentLabel(i)).join(", ")}`);
      }
      if (isPartyA && requesterRole) {
        intentParts.push(`Their Role: ${getCollaborationRoleLabel(requesterRole)}`);
      }
      if (isPartyA && expectedPartnerRole) {
        intentParts.push(`Expected Partner Role: ${getCollaborationRoleLabel(expectedPartnerRole)}`);
      }
      for (const part of intentParts) {
        checkNewPage(LH);
        yPosition = drawWrapped(part, PAGE_MARGIN, yPosition, CONTENT_WIDTH, helvetica, FS, BLACK_TEXT);
        yPosition -= 4;
      }
      yPosition -= 6;
    }

    // ── Business Overview ─────────────────────────────────────────────────
    if (c.description) {
      yPosition = drawWrapped(
        formatCompanyProfileTextForDisplay(c.description),
        PAGE_MARGIN,
        yPosition,
        CONTENT_WIDTH,
        helvetica,
        FS,
        BLACK_TEXT,
      );
      yPosition -= 8;
    }

    drawTwoColFields([
      ["COUNTRY", c.headquartersCountry || "N/A"],
      ["TRADE NAME", c.tradeName || "N/A"],
      ["BUSINESS ID", c.gtid || c.registrationNumber || "N/A"],
      ["WHO AM I", Array.isArray(c.whoAmI) && c.whoAmI.length > 0 ? c.whoAmI.map((r: string) => getWhoAmILabel(r)).join(", ") : "N/A"],
      ["INDUSTRY CATEGORY", Array.isArray(c.industryCategory) && c.industryCategory.length > 0 ? c.industryCategory.map((id: string) => getIndustryLabel(id)).join(", ") : "N/A"],
      ["SUB CATEGORY", Array.isArray(c.subIndustry) && c.subIndustry.length > 0 ? c.subIndustry.map((id: string) => getSubIndustryLabel(id)).join(", ") : "N/A"],
      ["COLLABORATION INTENT", Array.isArray(c.collaborationIntent) && c.collaborationIntent.length > 0 ? c.collaborationIntent.map((i: string) => getCollaborationIntentLabel(i)).join(", ") : "N/A"],
    ]);

    // ── Products & Services ───────────────────────────────────────────────
    const products = (c.productServices || []).filter((p: any) => p.type === ProductServiceType.PRODUCT);
    const services = (c.productServices || []).filter((p: any) => p.type === ProductServiceType.SERVICE);

    if (products.length > 0) {
      checkNewPage(20);
      page.drawText("PRODUCTS", { x: PAGE_MARGIN, y: yPosition, size: 8, font: helveticaBold, color: GRAY_TEXT });
      yPosition -= 12;
      for (const p of products) {
        checkNewPage(LH * 2 + 6);
        yPosition = drawWrapped(`• ${p.name || "Untitled"}`, PAGE_MARGIN, yPosition, CONTENT_WIDTH, helveticaBold, FS, BLACK_TEXT);
        if (p.description) {
          yPosition = drawWrapped(p.description, PAGE_MARGIN + 8, yPosition, CONTENT_WIDTH - 8, helvetica, FS, BLACK_TEXT);
        }
        yPosition -= 4;
      }
      yPosition -= 6;
    }

    if (services.length > 0) {
      checkNewPage(20);
      page.drawText("SERVICES", { x: PAGE_MARGIN, y: yPosition, size: 8, font: helveticaBold, color: GRAY_TEXT });
      yPosition -= 12;
      for (const s of services) {
        checkNewPage(LH * 2 + 6);
        yPosition = drawWrapped(`• ${s.name || "Untitled"}`, PAGE_MARGIN, yPosition, CONTENT_WIDTH, helveticaBold, FS, BLACK_TEXT);
        if (s.description) {
          yPosition = drawWrapped(s.description, PAGE_MARGIN + 8, yPosition, CONTENT_WIDTH - 8, helvetica, FS, BLACK_TEXT);
        }
        yPosition -= 4;
      }
      yPosition -= 6;
    }

    // ── Capabilities & Offerings ──────────────────────────────────────────
    if (c.coreCapabilities?.length > 0) {
      drawField("CORE CAPABILITIES", c.coreCapabilities.join(", "));
    }
    if (c.secondaryCapabilities?.length > 0) {
      drawField("SECONDARY CAPABILITIES", c.secondaryCapabilities.join(", "));
    }
    if (c.keyDifferentiators) {
      drawField("KEY DIFFERENTIATORS", c.keyDifferentiators);
    }
    if (c.customerOfferHighlight) {
      const offers = Array.isArray(c.customerOfferHighlight)
        ? c.customerOfferHighlight.filter(Boolean).join(", ")
        : c.customerOfferHighlight;
      if (offers) drawField("CUSTOMER OFFERS", offers);
    }
    if (c.technologies?.length > 0) {
      drawField("TECHNOLOGY & INFRASTRUCTURE", c.technologies.join(", "));
    }
    if (c.additionalTechInfrastructure?.length > 0) {
      drawField("ADDITIONAL TECHNOLOGY & INFRASTRUCTURE", c.additionalTechInfrastructure.map((i: string) => getAdditionalTechInfraLabel(i)).join(", "));
    }

    // ── Geography & Markets ───────────────────────────────────────────────
    drawField("HEADQUARTERS",
      c.headquartersCity && c.headquartersCountry
        ? `${c.headquartersCity}, ${c.headquartersCountry}`
        : c.headquartersCountry || "N/A"
    );

    if (c.operatingCountries?.length > 0) {
      drawField("OPERATING COUNTRIES", c.operatingCountries.join(", "));
    }
    if (c.preferredCollaborationRegions?.length > 0) {
      drawField("PREFERRED COLLABORATION REGIONS", c.preferredCollaborationRegions.map((r: string) => getRegionLabel(r)).join(", "));
    }

    // ── Contact Details ───────────────────────────────────────────────────
    drawTwoColFields([
      ["CONTACT NAME", c.user?.name || "N/A"],
      ["DESIGNATION", c.user?.designation || "N/A"],
      ["PHONE", c.user?.phoneNumber || (c.phoneCountryCode && c.phoneNumber ? `${c.phoneCountryCode} ${c.phoneNumber}` : "N/A")],
      ["EMAIL", c.email || "N/A"],
    ]);

    if (c.website) {
      drawField("WEBSITE", c.website);
    }
    // ── System Verification Details ───────────────────────────────────────
    const metadata = c.externalVerificationLogs?.[0]?.metadata;
    if (metadata) {
      drawTwoColFields([
        ["VERIFIED COMPANY NAME", metadata.companyName || "N/A"],
        ["COMPANY ADDRESS", metadata.companyAddress || "N/A"],
        ["DATE OF INCORPORATION", metadata.dateOfIncorporation || "N/A"],
        ["REGISTRATION NUMBER", metadata.registrationNumber || "N/A"],
        ["COMPANY TYPE", metadata.companyType || "N/A"],
        ["COMPANY STATUS", metadata.companyStatus || "N/A"],
      ]);
    }

    yPosition -= 6;
  };

  drawSubHeader(`4.1 ${partyA.legalName || "Party A"} (Party A) – Company Profile`);
  drawCompanyProfile(partyA, true);

  checkNewPage(30);
  drawSubHeader(`4.2 ${partyB.legalName || "Party B"} (Party B) – Company Profile`);
  drawCompanyProfile(partyB, false);

  // ===== SECTION 5: DOCUMENTS SHARED =====
  drawSectionHeader("5. Documents Shared by Participants");
  checkNewPage(20);
  yPosition = drawWrapped(
    "The following documents were voluntarily shared by participating companies during the collaboration evaluation process.",
    PAGE_MARGIN,
    yPosition,
    CONTENT_WIDTH,
    helvetica,
    FS,
    BLACK_TEXT
  );
  yPosition -= 10;

  if (sharedDocuments.length === 0) {
    checkNewPage(LH);
    page.drawText("No documents have been shared yet.", {
      x: PAGE_MARGIN,
      y: yPosition,
      size: FS,
      font: helveticaOblique,
      color: GRAY_TEXT,
    });
    yPosition -= 20;
  } else {
    drawTable(
      ["Party", "Document Type"],
      sharedDocuments.map((doc) => [doc.isOwnDocument ? (partyA.legalName || "Party A") : (partyB.legalName || "Party B"), doc.fileName]),
      [100, CONTENT_WIDTH - 100]
    );
  }

  // ===== SECTION 6: QUESTIONNAIRE FRAMEWORK =====
  drawSectionHeader("6. Collaboration Questionnaire Framework");
  checkNewPage(20);
  yPosition = drawWrapped(
    "Both companies completed the GTsetu structured questionnaire designed to assess compatibility across four primary dimensions.",
    PAGE_MARGIN,
    yPosition,
    CONTENT_WIDTH,
    helvetica,
    FS,
    BLACK_TEXT
  );
  yPosition -= 10;

  drawTable(
    ["Evaluation Area", "Description"],
    [
      ["Technical", "Capabilities, technology readiness, operational compatibility"],
      ["Commercial", "Pricing structures, exclusivity preferences, collaboration models"],
      ["Legal", "Intellectual property considerations, compliance expectations"],
      ["Financial", "Payment structures, investment expectations, financial readiness"],
    ],
    [130, CONTENT_WIDTH - 130]
  );

  checkNewPage(16);
  yPosition = drawWrapped(
    "The questionnaire responses were analysed to generate a structured alignment overview.",
    PAGE_MARGIN,
    yPosition,
    CONTENT_WIDTH,
    helvetica,
    FS,
    BLACK_TEXT
  );
  yPosition -= 12;

  // ===== SECTION 7: ALIGNMENT OVERVIEW =====
  drawSectionHeader("7. Collaboration Alignment Overview");

  const GRADE_LABELS: Record<string, string> = {
    alignment: "Alignment",
    discussion: "Requires Discussion",
    key_decision: "Key Decision",
    clarification: "Insufficient data",
  };

  if (gradingResult && gradingResult.results.length > 0) {
    checkNewPage(30);
    yPosition = drawWrapped(
      "The following alignment overview was produced based on the structured questionnaire responses submitted by both participating companies.",
      PAGE_MARGIN, yPosition, CONTENT_WIDTH, helvetica, FS, BLACK_TEXT
    );
    yPosition -= 12;

    // Group results by sectionId (preserving order)
    const sectionOrder: string[] = [];
    const sectionGroups: Record<string, QuestionGradingResult[]> = {};
    for (const r of gradingResult.results) {
      if (!sectionGroups[r.sectionId]) {
        sectionGroups[r.sectionId] = [];
        sectionOrder.push(r.sectionId);
      }
      sectionGroups[r.sectionId].push(r);
    }

    sectionOrder.forEach((sectionId, idx) => {
      const sectionTitle = sectionTitleMap?.[sectionId] ?? sectionId;
      checkNewPage(30);
      drawSubHeader(`7.${idx + 1}  ${sectionTitle}`);
      yPosition -= 4;

      for (const result of sectionGroups[sectionId]) {
        const gradeLabel = GRADE_LABELS[result.category] ?? result.category;
        const description = getGradeDescription(
          gradingResult.questionnaireId,
          result.questionId,
          result.category
        );

        // Pre-calculate total block height so question + description stay together
        const qLines = splitLines(result.questionText, CONTENT_WIDTH - 120, helveticaBold, FS);
        const dLines = description ? splitLines(description, CONTENT_WIDTH - 8, helveticaOblique, FS - 1) : [];
        const qOpts = questionOptionMap?.[result.questionId];
        const answerExtraH = qOpts ? 4 + 2 * (FS * 1.5) : 0;
        const blockH = qLines.length * (FS * 1.5) + (dLines.length > 0 ? 2 + dLines.length * (FS * 1.5) : 0) + 12 + answerExtraH;
        checkNewPage(blockH);

        // Question text (bold)
        const qStartY = yPosition;
        yPosition = drawWrapped(result.questionText, PAGE_MARGIN, yPosition, CONTENT_WIDTH - 120, helveticaBold, FS, BLACK_TEXT);

        // Grade label — right-aligned, vertically centred on the question text
        page.drawText(gradeLabel, {
          x: PAGE_WIDTH - PAGE_MARGIN - helveticaBold.widthOfTextAtSize(gradeLabel, FS),
          y: qStartY - FS,
          size: FS,
          font: helveticaBold,
          color: PRIMARY_COLOR,
        });

        // Description text (italic, dimmed)
        if (description) {
          yPosition -= 2;
          yPosition = drawWrapped(description, PAGE_MARGIN + 8, yPosition, CONTENT_WIDTH - 8, helveticaOblique, FS - 1, GRAY_TEXT);
        }

        // Answer lines
        if (qOpts && myAnswers && partnerAnswers) {
          const myLabel = resolveAnswerLabel(qOpts, myAnswers[result.questionId]);
          const partnerLabel = resolveAnswerLabel(qOpts, partnerAnswers[result.questionId]);
          yPosition -= 4;
          yPosition = drawWrapped(`Party A: ${myLabel}`, PAGE_MARGIN + 8, yPosition, CONTENT_WIDTH - 16, helveticaOblique, FS - 1, GRAY_TEXT);
          yPosition = drawWrapped(`Party B: ${partnerLabel}`, PAGE_MARGIN + 8, yPosition, CONTENT_WIDTH - 16, helveticaOblique, FS - 1, GRAY_TEXT);
        }

        yPosition -= 12;
      }
      yPosition -= 4;
    });
  } else {
    checkNewPage(LH * 2 + 8);
    yPosition = drawWrapped(
      "Alignment overview will be available once both parties have completed the questionnaire.",
      PAGE_MARGIN, yPosition, CONTENT_WIDTH, helveticaOblique, FS, GRAY_TEXT
    );
    yPosition -= 20;
  }

  // ===== SECTION 8: CONCLUSION =====
  drawSectionHeader("8. Conclusion");
  for (const para of [
    "This report summarizes the information provided by the participating companies and the resulting alignment overview generated through the GTsetu platform.",
    "The findings presented in this report reflect the responses submitted by the respective companies during the structured evaluation process. The report is intended to assist both parties in understanding areas of alignment and topics that may require further discussion as they explore potential collaboration opportunities.",
    "GTsetu's role is limited to facilitating the initial connection and providing the structured alignment framework reflected in this report. Any subsequent discussions, negotiations, due diligence, agreements, or business arrangements are conducted independently between the participating companies.",
  ]) {
    checkNewPage(30);
    yPosition = drawWrapped(para, PAGE_MARGIN, yPosition, CONTENT_WIDTH, helvetica, FS, BLACK_TEXT);
    yPosition -= 6;
  }
  yPosition -= 8;

  // ===== SECTION 9: DISCLAIMER =====
  drawSectionHeader("9. Disclaimer");
  for (const para of [
    "This report is generated based on information voluntarily submitted by participating companies through the GTsetu platform. GTsetu does not verify the accuracy, completeness, or authenticity of the information or documents provided.",
    "The platform does not provide legal, financial, or commercial advice, and no recommendation or endorsement of either party is implied.",
    "Participating companies are encouraged to conduct their own independent evaluation and due diligence before entering into any business arrangements.",
  ]) {
    checkNewPage(30);
    yPosition = drawWrapped(para, PAGE_MARGIN, yPosition, CONTENT_WIDTH, helvetica, FS, BLACK_TEXT);
    yPosition -= 6;
  }

  // ===== HEADER LOGO + FOOTER =====
  const pages = pdfDoc.getPages();
  pages.forEach((p, idx) => {
    // Logo — top right of every page
    if (logoImage) {
      p.drawImage(logoImage, {
        x: PAGE_WIDTH - PAGE_MARGIN - logoDims.width,
        y: PAGE_HEIGHT - PAGE_MARGIN + 5,
        width: logoDims.width,
        height: logoDims.height,
      });
    }
    // Page number — bottom right
    p.drawText(`${idx + 1} / ${pages.length}`, {
      x: PAGE_WIDTH - 50,
      y: 20,
      size: 8,
      font: helvetica,
      color: GRAY_TEXT,
    });
    // Footer label — bottom left
    p.drawText("GTsetu – Collaboration Alignment Report – Confidential", {
      x: PAGE_MARGIN,
      y: 20,
      size: 7,
      font: helvetica,
      color: GRAY_TEXT,
    });
  });

  // ===== SAVE & DOWNLOAD =====
  const pdfBytes = await pdfDoc.save();
  const blob = new Blob([pdfBytes as BlobPart], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `CollaborationReport_${(partyA.legalName || "PartyA").replace(/\s+/g, "_")}_${(partyB.legalName || "PartyB").replace(/\s+/g, "_")}.pdf`;
  link.click();
  URL.revokeObjectURL(url);
}
